import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Truck, FileText, MapPin } from "lucide-react";

const REGULAR_STAGES = [
  { key: "assigned", label: "Betalt" },
  { key: "received", label: "Enhet mottatt" },
  { key: "in_progress", label: "Under reparasjon" },
  { key: "sent_back", label: "Sendt tilbake" },
  { key: "completed", label: "Fullført" },
];
// status -> stage index
const STAGE_INDEX = {
  assigned: 0,
  received: 1,
  in_progress: 2,
  repair_done: 2,
  sent_back: 3,
  completed: 4,
};

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null));
  }, [id]);

  if (!order) return <div className="tk-container py-20 text-slate-500" data-testid="tracking-loading">Laster …</div>;

  const currentStage = STAGE_INDEX[order.status] ?? -1;

  return (
    <div className="tk-container py-16 max-w-3xl" data-testid="order-tracking-page">
      <Link to="/mine-bestillinger" className="text-sm text-slate-500 hover:text-[#0B1C30]">
        ← Tilbake til mine bestillinger
      </Link>
      <p className="tk-overline mt-6 mb-3 font-mono">#{order.id.slice(0, 8)}</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-2">
        {order.product_name}
      </h1>
      <p className="text-sm text-slate-500 mb-10">
        {order.service_name} · {order.fixer_display_name}
      </p>

      {/* Timeline */}
      {!order.is_diagnostic && (
        <div className="tk-card mb-8" data-testid="status-timeline">
          <p className="tk-overline mb-6">Status</p>
          <ol className="relative space-y-6">
            {REGULAR_STAGES.map((s, i) => {
              const done = i <= currentStage;
              const active = i === currentStage;
              return (
                <li key={s.key} className="flex items-start gap-4" data-testid={`stage-${s.key}`}>
                  <div className="relative flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                        done ? "bg-[#4A7C59] border-[#4A7C59] text-white" : "bg-white border-[#E2E8F0] text-slate-400"
                      }`}
                    >
                      {done ? <Check size={16} /> : <span className="text-xs font-semibold">{i + 1}</span>}
                    </div>
                    {i < REGULAR_STAGES.length - 1 && (
                      <div className={`w-px flex-1 mt-1 ${done ? "bg-[#4A7C59]" : "bg-[#E2E8F0]"}`} style={{ minHeight: 24 }} />
                    )}
                  </div>
                  <div className="pb-2">
                    <p className={`font-medium ${active ? "text-[#0B1C30]" : done ? "text-slate-700" : "text-slate-400"}`}>
                      {s.label}
                    </p>
                    {active && <p className="text-xs text-slate-500 mt-1">Pågår nå</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Diagnostic-specific findings panel */}
      {order.is_diagnostic && order.diagnostic_findings && (
        <div className="tk-card mb-8">
          <p className="tk-overline mb-3 flex items-center gap-2"><FileText size={14} /> Diagnose</p>
          <p className="text-sm mb-1"><span className="text-slate-500">Funn:</span> {order.diagnostic_findings}</p>
          <p className="text-sm mb-1"><span className="text-slate-500">Anbefalt:</span> {order.recommended_repair}</p>
          <p className="text-sm"><span className="text-slate-500">Tilleggspris:</span> kr {Number(order.recommended_price || 0).toFixed(0)}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="tk-card">
          <p className="tk-overline mb-3 flex items-center gap-2"><Truck size={14} /> Levering</p>
          <p className="text-sm">{order.delivery_method === "shipping" ? "Bring-frakt" : "Selv-levering på verksted"}</p>
          {order.shipping_label_url && (
            <a href={order.shipping_label_url} target="_blank" rel="noreferrer" className="text-xs text-[#0B1C30] underline mt-2 inline-block">
              Last ned fraktetikett →
            </a>
          )}
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-3 flex items-center gap-2"><MapPin size={14} /> Verksted</p>
          <p className="text-sm">{order.fixer_display_name}</p>
        </div>
      </div>

      <div className="tk-card mt-4">
        <p className="tk-overline mb-3">Beløp</p>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Del</span><span>kr {Number(order.part_price).toFixed(0)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Arbeid</span><span>kr {Number(order.service_fee).toFixed(0)}</span>
        </div>
        <div className="tk-hairline my-2" />
        <div className="flex justify-between font-semibold">
          <span>Total</span><span>kr {Number(order.total_price).toFixed(0)}</span>
        </div>
      </div>
    </div>
  );
}
